import { useEffect } from 'react';

const STORAGE_KEY = 'CHRONOSPHERE_APP_LOAD_TIMESTAMP';
const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000;
// Check every hour (3600000ms) to see if 2 weeks have passed
const CHECK_INTERVAL_MS = 60 * 60 * 1000;

/**
 * Hook that forces a refresh if the active browser session has not been reloaded in 2 weeks.
 * This tracks the last reload time using sessionStorage and refreshes if more than 2 weeks have passed.
 * This ensures users get the latest version of the app even if they keep a tab open for an extended period.
 */
export function useForceRefreshAfterPeriod() {
  useEffect(() => {
    const checkAndRefresh = () => {
      const now = Date.now();
      const storedTimestamp = sessionStorage.getItem(STORAGE_KEY);
      const timestamp = storedTimestamp ? parseInt(storedTimestamp, 10) : null;

      if (timestamp && !isNaN(timestamp) && timestamp > 0) {
        const timeSinceLastReload = now - timestamp;

        if (timeSinceLastReload > TWO_WEEKS_MS) {
          // Clear the stored timestamp and force refresh
          sessionStorage.removeItem(STORAGE_KEY);
          window.location.reload();
          return;
        }
      } else {
        // Store the initial timestamp if it doesn't exist
        sessionStorage.setItem(STORAGE_KEY, now.toString());
      }
    };

    // Check immediately on mount
    checkAndRefresh();

    // Set up interval to check periodically
    const intervalId = setInterval(checkAndRefresh, CHECK_INTERVAL_MS);

    // Add window focus handler to call checkAndRefresh
    const handleFocus = () => {
      checkAndRefresh();
    };
    window.addEventListener('focus', handleFocus);

    // Clean up the interval and the event listener on unmount
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);
}
